import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import defaultNames from "src/utils/horseDefaultNames";
import { getUserPermissions } from "src/utils/permissionsUtils";

export default function HorseNamesConfig({
  gameConfig,
  setGameConfig,
  loading,
}) {
  const { isViewer } = getUserPermissions();
  const [selectedTab, setSelectedTab] = useState(0);

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French' }
  ];

  const getCurrentLanguage = () => languages[selectedTab].code;

  // Get current names for the selected language with fallback
  const getCurrentNames = () => {
    const lang = getCurrentLanguage();
    const namesI18n = gameConfig.namesI18n || {};
    const currentLangNames = namesI18n[lang];
    const fallbackNames = gameConfig.names;

    if (currentLangNames && currentLangNames.length > 0) {
      return currentLangNames;
    } else if (fallbackNames && fallbackNames.length > 0) {
      return fallbackNames;
    } else {
      return Array(25).fill(null).map((_, index) => defaultNames[index] || "");
    }
  };

  const handleNameChange = (index, value) => {
    const lang = getCurrentLanguage();
    const currentNames = [...getCurrentNames()];
    currentNames[index] = value;

    const namesI18n = { ...gameConfig.namesI18n };
    if (!namesI18n[lang]) {
      namesI18n[lang] = [];
    }
    namesI18n[lang] = currentNames;

    setGameConfig({ ...gameConfig, namesI18n });
  };

  const handleAddHorseName = () => {
    const lang = getCurrentLanguage();
    const currentNames = getCurrentNames();
    const newNames = [...currentNames, ""];

    const namesI18n = { ...gameConfig.namesI18n };
    if (!namesI18n[lang]) {
      namesI18n[lang] = [];
    }
    namesI18n[lang] = newNames;

    setGameConfig({ ...gameConfig, namesI18n });
  };

  const handleDeleteHorseName = (index) => {
    const lang = getCurrentLanguage();
    const currentNames = getCurrentNames();
    if (currentNames.length <= 1) return;

    const newNames = currentNames.filter((_, i) => i !== index);

    const namesI18n = { ...gameConfig.namesI18n };
    if (!namesI18n[lang]) {
      namesI18n[lang] = [];
    }
    namesI18n[lang] = newNames;

    setGameConfig({ ...gameConfig, namesI18n });
  };

  return (
    <Box sx={{ mt: 2, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Horse Names Configuration
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Configure the names for all horses in the racing game
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => setSelectedTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#8e24aa',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#7b1fa2',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#8e24aa',
            },
          }}
        >
          {languages.map((lang, index) => (
            <Tab key={lang.code} label={lang.label} disabled={loading} />
          ))}
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        {getCurrentNames().map((name, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`name-${index}`}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                label={`Horse ${index + 1}`}
                value={name}
                onChange={(e) => handleNameChange(index, e.target.value)}
                variant="outlined"
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#ffffff",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#8e24aa",
                    fontWeight: 500,
                  },
                }}
              />
              {!isViewer && (
                <IconButton
                  onClick={() => handleDeleteHorseName(index)}
                  disabled={loading || getCurrentNames().length <= 1}
                  size="small"
                  sx={{
                    color:
                      getCurrentNames().length <= 1 ? "#ccc" : "#d32f2f",
                    "&:hover": {
                      backgroundColor:
                        getCurrentNames().length <= 1
                          ? "transparent"
                          : "#ffcdd2",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddHorseName}
          disabled={loading}
          sx={{
            backgroundColor: "#8e24aa",
            "&:hover": {
              backgroundColor: "#7b1fa2",
            },
          }}
        >
          Add New Horse Name
        </Button>
      </Box>
    </Box>
  );
}
